import React, { useMemo, useState } from "react";
import { CloudUpload } from "@mui/icons-material";
import { Alert, Box, Paper, Typography } from "@mui/material";

import { useMultiLayerFeatures } from "fond/draw/MultiLayerEdit/useMultiLayerFeatures";
import { useGetImportLayers } from "fond/hooks/useGetImportLayers";
import { addImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { ISGLayerIds } from "fond/types";
import { removeDrawingProperties } from "fond/utils/geojson";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { isImportInProgress } from "fond/utils/layers";
import { ConfirmModal, LinearProgressWithLabel, StackedNavigationButton, SvgIcon, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

const HubsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const layerIdMapping = useMemo(() => ({ [ISGLayerIds.solveHubs]: ISGLayerIds.hubs }), []);
  const { features: solveHubs, isLoading } = useMultiLayerFeatures(versionId, [ISGLayerIds.solveHubs], {
    layerIdMapping,
  });
  const hasHubsLayer = !!useGetImportLayers(versionId, Object.values(ISGLayerIds))?.get(ISGLayerIds.hubs)?.layer;
  const importState = useAppSelector((state) => state.imports[versionId]?.[ISGLayerIds.hubs]);
  const importing = isImportInProgress(importState?.status);
  const { open, clear } = useStackedNavigationContext();

  const handleOnDrawHubsButtonClick = () => {
    open("design-hubs-draw");
  };

  const handleOnImportButtonClick = () => {
    const collection = removeDrawingProperties(featureCollection(solveHubs));
    const file = new File([JSON.stringify(collection)], "geojson.json", {
      type: "text/plain",
    });
    dispatch(addImport({ versionId, layerKey: ISGLayerIds.hubs, displayName: "Hubs", files: [file] }));
    setShowConfirmModal(false);
  };

  const handleShowConfirmModal = () => {
    setShowConfirmModal(true);
  };

  const handleCancelReplace = () => {
    setShowConfirmModal(false);
  };

  const onBack = () => {
    clear();
  };

  return (
    <Box>
      <StackedNavigationHeader title="Hubs" onBackButtonClick={onBack} />
      <ConfirmModal
        open={showConfirmModal}
        header="Replace existing hubs?"
        confirmText="Continue"
        cancelText="Cancel"
        color="error"
        content={
          <Typography variant="body2">This will replace all existing hub data with the solve output. This action cannot be undone.</Typography>
        }
        onConfirm={handleOnImportButtonClick}
        onCancel={handleCancelReplace}
      />
      {importing && (
        <>
          <Alert severity="info" sx={{ my: 1 }}>
            FOND is now processing your data, and it will be visible on the map shortly.
          </Alert>
          <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
            <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing hubs" subtext="Processing..." />
          </Paper>
        </>
      )}
      {!importing && (
        <>
          <StackedNavigationButton
            data-testid="draw-new-hubs-button"
            icon={<SvgIcon icon="polygon" />}
            title="Edit hubs"
            subtitle="Edit or draw your own hubs on the map"
            onClick={handleOnDrawHubsButtonClick}
          />
          <StackedNavigationButton
            data-testid="import-hubs-button"
            icon={<CloudUpload />}
            title={hasHubsLayer ? "Replace hubs" : "Import solve hubs"}
            subtitle={hasHubsLayer ? "Reset hubs to solve output" : "Import hubs from solve output"}
            onClick={hasHubsLayer ? handleShowConfirmModal : handleOnImportButtonClick}
            disabled={isLoading || solveHubs?.length === 0}
          />
        </>
      )}
    </Box>
  );
};

export default HubsPanel;
